import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableRow,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UndoIcon from '@mui/icons-material/Undo';

import { fetchPastOrders, fetchInvoice, makeReturnDoc, submitReturn } from '../api/invoice';

/**
 * Returns dialog — two-step flow:
 *
 * Step 1: Search and select a past POS Invoice to return against.
 * Step 2: Review items, select which to return, confirm refund.
 */
export default function ReturnsDialog({ open, onClose, onSuccess }) {
  // Step state
  const [step, setStep] = useState('search'); // 'search' | 'review'

  // Search step
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [pastOrders, setPastOrders] = useState([]);

  // Review step
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [returnDoc, setReturnDoc] = useState(null);
  const [selectedItems, setSelectedItems] = useState({}); // { idx: true/false }
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load recent orders when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function loadRecent() {
      setSearching(true);
      try {
        const orders = await fetchPastOrders({ searchTerm: '', status: 'Paid', limit: 20 });
        if (!cancelled) setPastOrders(orders);
      } catch {
        // ignore — user can still search manually
      } finally {
        if (!cancelled) setSearching(false);
      }
    }

    loadRecent();
    return () => { cancelled = true; };
  }, [open]);

  // Search past orders (works with empty search term too)
  const handleSearch = useCallback(async () => {
    setSearching(true);
    setError(null);
    try {
      const orders = await fetchPastOrders({
        searchTerm: searchTerm.trim(),
        status: 'Paid',
        limit: 20,
      });
      setPastOrders(orders);
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  // Select an invoice to return
  const handleSelectInvoice = async (invoiceName) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the full invoice and generate return doc
      const [invoice, retDoc] = await Promise.all([
        fetchInvoice(invoiceName),
        makeReturnDoc(invoiceName),
      ]);

      setOriginalInvoice(invoice);
      setReturnDoc(retDoc);

      // Pre-select all items for return
      const selected = {};
      (retDoc.items || []).forEach((item, idx) => {
        selected[idx] = true;
      });
      setSelectedItems(selected);
      setStep('review');
    } catch (err) {
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  // Toggle item selection
  const toggleItem = (idx) => {
    setSelectedItems((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Submit the return
  const handleSubmitReturn = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // Filter items to only selected ones
      const itemsToReturn = (returnDoc.items || []).filter((_, idx) => selectedItems[idx]);
      if (itemsToReturn.length === 0) {
        setError('Select at least one item to return.');
        setSubmitting(false);
        return;
      }

      // Build the return document with only selected items
      const doc = {
        ...returnDoc,
        items: itemsToReturn,
      };

      const result = await submitReturn(doc);

      onSuccess?.({
        returnInvoice: result,
        originalInvoice,
      });
    } catch (err) {
      const msg = err?.response?.data?._server_messages || err.message || 'Return failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset when dialog closes
  const handleClose = () => {
    setStep('search');
    setSearchTerm('');
    setPastOrders([]);
    setOriginalInvoice(null);
    setReturnDoc(null);
    setSelectedItems({});
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={!submitting ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <UndoIcon />
          <span>{step === 'search' ? 'Return / Exchange' : 'Review Return'}</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Search past invoices */}
        {step === 'search' && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by invoice number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="contained" onClick={handleSearch} disabled={searching}>
                {searching ? <CircularProgress size={20} /> : 'Search'}
              </Button>
            </Stack>

            {loading && (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Stack>
            )}

            {pastOrders.length > 0 && !loading && (
              <List dense sx={{ maxHeight: 350, overflow: 'auto' }}>
                {pastOrders.map((order) => (
                  <ListItemButton
                    key={order.name}
                    onClick={() => handleSelectInvoice(order.name)}
                  >
                    <ReceiptLongIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={order.name}
                      secondary={`${order.customer || ''} — ${order.grand_total?.toFixed(2) || ''} — ${order.posting_date || ''}`}
                    />
                    <Chip
                      label={order.status}
                      size="small"
                      color={order.status === 'Paid' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </ListItemButton>
                ))}
              </List>
            )}

            {pastOrders.length === 0 && !searching && !loading && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No invoices found
              </Typography>
            )}
          </>
        )}

        {/* Step 2: Review items and confirm return */}
        {step === 'review' && returnDoc && (
          <>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Original Invoice</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {originalInvoice?.name}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Customer</Typography>
                <Typography variant="body2">{originalInvoice?.customer}</Typography>
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select items to return
            </Typography>

            <Table size="small">
              <TableBody>
                {(returnDoc.items || []).map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={!!selectedItems[idx]}
                        onChange={() => toggleItem(idx)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.item_name || item.item_code}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Qty: {Math.abs(item.qty)} x {item.rate?.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {Math.abs((item.qty || 0) * (item.rate || 0)).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ my: 1.5 }} />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>Refund Total</Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {(returnDoc.items || [])
                  .filter((_, idx) => selectedItems[idx])
                  .reduce((sum, item) => sum + Math.abs((item.qty || 0) * (item.rate || 0)), 0)
                  .toFixed(2)}
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'review' && (
          <Button onClick={() => setStep('search')} disabled={submitting} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {step === 'review' && (
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitReturn}
            disabled={submitting || Object.values(selectedItems).every((v) => !v)}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Process Return'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
